'use client'

import { motion } from 'motion/react'
import {
  Download,
  Monitor,
  CheckCircle,
  Zap,
  Shield,
  Globe,
  ChevronRight,
} from 'lucide-react'
import { SectionLabel } from './player-card'

const FEATURES = [
  {
    icon: Zap,
    label: 'Real-time MMR tracking',
    desc: 'Live race result capture while you play',
  },
  {
    icon: Shield,
    label: 'Secure account sync',
    desc: 'Encrypted local storage + cloud backup',
  },
  {
    icon: Globe,
    label: 'Online leaderboard',
    desc: 'Compare MMR with global players',
  },
]

const STEPS = [
  {
    n: '01',
    title: 'Download the mod',
    desc: 'Download the mod package.',
  },
  {
    n: '02',
    title: 'Install it in your emulator',
    desc: 'Install the mod package in your emulator of choice.',
  },
  {
    n: '03',
    title: 'Launch MK8D',
    desc: 'Start the game after the mod is installed.',
  },
  {
    n: '04',
    title: 'Connect your account',
    desc: 'Sign in to sync races and MMR in real time.',
  },
]

export function DownloadSection() {
  return (
    <section
      id="download"
      className="relative py-28 px-6 overflow-hidden"
      style={{ background: '#08080e' }}
    >
      <div
        className="absolute top-0 left-6 right-6 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(0,234,255,0.15), transparent)',
        }}
      />

      {/* BG accent */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 100%, rgba(0,119,255,0.12), transparent 65%)',
        }}
      />

      <div className="max-w-5xl mx-auto relative">
        <SectionLabel label="RYUJINX MOD" />

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Left: hero card */}
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Main download card */}
            <div
              className="relative rounded-3xl p-8 mb-6 overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, rgba(0,119,255,0.12) 0%, rgba(0,234,255,0.06) 100%)',
                border: '1px solid rgba(0,234,255,0.18)',
              }}
            >
              {/* Decorative grid */}
              <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(0,234,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,234,255,1) 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                }}
              />

              <div className="relative">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background: 'linear-gradient(135deg, #0077ff, #00eaff)',
                  }}
                >
                  <Monitor size={28} color="#08080e" />
                </div>

                <h3
                  style={{
                    fontFamily: "'Russo One', sans-serif",
                    color: '#fff',
                    fontSize: '26px',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}
                  className="mb-2"
                >
                  MK8D Stats
                  <br />
                  Mod Package
                </h3>
                <p
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.45)',
                    lineHeight: 1.6,
                  }}
                  className="mb-6"
                >
                  Install the mod in your emulator of choice, then launch MK8D
                  to sync races and MMR.
                </p>

                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle size={13} color="#00ff88" />
                  <span
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.4)',
                      letterSpacing: '0.06em',
                    }}
                  >
                    v1.0.0 · Mod Package (.zip) · 9.3 KB
                  </span>
                </div>

                {/* Download button */}
                <div className="flex flex-col gap-3">
                  <motion.a
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    href="/downloads/mk8d-mod-v1.0.0.zip"
                    download
                    className="flex items-center justify-center gap-3 py-3.5 rounded-xl font-bold relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(90deg, #0077ff, #00eaff)',
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      fontSize: '15px',
                      letterSpacing: '0.15em',
                      color: '#08080e',
                    }}
                  >
                    <Download size={16} />
                    DOWNLOAD MOD
                  </motion.a>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map(({ icon: Icon, label, desc }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.07 }}
                  className="rounded-xl p-4"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <Icon size={15} color="#00eaff" className="mb-2" />
                  <p
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      fontSize: '13px',
                      color: '#fff',
                      letterSpacing: '0.02em',
                    }}
                    className="mb-0.5"
                  >
                    {label}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.3)',
                      lineHeight: 1.4,
                    }}
                  >
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: setup steps */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{
              delay: 0.15,
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <p
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.2em',
                color: 'rgba(255,255,255,0.3)',
                fontSize: '11px',
              }}
              className="mb-6"
            >
              SETUP GUIDE
            </p>

            <div className="flex flex-col gap-4">
              {STEPS.map(({ n, title, desc }, i) => (
                <motion.div
                  key={n}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                  className="flex gap-5 items-start p-5 rounded-2xl transition-colors group cursor-default"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: 'rgba(0,234,255,0.08)',
                      border: '1px solid rgba(0,234,255,0.2)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Russo One', sans-serif",
                        fontSize: '13px',
                        color: '#00eaff',
                      }}
                    >
                      {n}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: '17px',
                        color: '#fff',
                        letterSpacing: '0.02em',
                      }}
                      className="mb-1"
                    >
                      {title}
                    </p>
                    <p
                      style={{
                        fontFamily: "'Barlow', sans-serif",
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.4)',
                        lineHeight: 1.5,
                      }}
                    >
                      {desc}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    color="rgba(255,255,255,0.15)"
                    className="flex-shrink-0 mt-1"
                  />
                </motion.div>
              ))}
            </div>

            {/* FAQ links */}
            <div
              className="mt-8 p-5 rounded-2xl"
              style={{
                background: 'rgba(255,230,0,0.04)',
                border: '1px solid rgba(255,230,0,0.12)',
              }}
            >
              <p
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: '13px',
                  color: '#ffe600',
                  letterSpacing: '0.1em',
                }}
                className="mb-2"
              >
                ⚠️ MOD REQUIREMENTS
              </p>
              <p
                style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.4)',
                  lineHeight: 1.6,
                }}
              >
                Requires MK8D v3.0.3 with DLC and is compatible with CTGPDX.
                CTGPDX: <a href="https://www.ctgpdx.com/" target="_blank" rel="noreferrer" style={{ color: '#00eaff' }}>https://www.ctgpdx.com/</a>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
